import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { AddOnsManager } from '@ctct/application-layer';
import {
  BillingStrategy,
  isStripeBillingStrategy,
  PLAN_GROUP_IDS,
  type BillingStrategyQuery,
} from '@ctct/domain-layer';
import type { AddOnsHooks, AddOnsPageStore, EmailPlansHooks, PrepaySelectHooks } from '@ctct/state-client-layer';
import {
  useBillingAccount,
  useAddOnsFacade,
  usePaymentPlanFacade,
  usePlanDetail,
  useIsLegacyPlanGroup,
  useUpgradePlanPromotion,
} from '@ctct/state-server-layer';

import { usePlanIdForSmsBundleAddOns } from '../../hooks/usePlanIdForSmsBundleAddOns';
import { useWidgetLifecycle } from '../../hooks/useWidgetLifecycle';
import { useIsSingleBillChildAccountState } from '../helpers/useIsSingleBillChildAccount';

interface Props {
  initializer: Pick<AddOnsManager, 'addOnsInit'>;
  pageStore: AddOnsPageStore;
  hooks: AddOnsHooks;
  externalHooks: {
    useSelectedEmailPlanId: EmailPlansHooks['useSelectedEmailPlanId'];
    usePrepay: PrepaySelectHooks['usePrepay'];
  };
  billingStrategy: BillingStrategyQuery;
  isSmsBundle?: boolean;
  isValueFocusedMWebPlanSelectionEnabled?: boolean;
}

const resolveBillingStrategyEnum = (billingStrategy: BillingStrategyQuery): BillingStrategy =>
  billingStrategy.isPending ? BillingStrategy.InHouse : billingStrategy.data;

export const useAddOnsWidget = ({
  initializer,
  pageStore,
  hooks,
  externalHooks,
  billingStrategy,
  isSmsBundle = false,
  isValueFocusedMWebPlanSelectionEnabled = false,
}: Props) => {
  const { t } = useTranslation();

  const selectedEmailPlanId = externalHooks.useSelectedEmailPlanId();
  const includePurchased = hooks.useIncludePurchased();

  const planIdForAddOnsRequest = usePlanIdForSmsBundleAddOns({ selectedEmailPlanId, isSmsBundle });

  const planDetail = usePlanDetail();
  const isMicroEmail = planDetail.data?.isMicroEmail() ?? false;
  const isStripeUser = isStripeBillingStrategy(billingStrategy);
  const resolvedBillingStrategy = resolveBillingStrategyEnum(billingStrategy);
  const { isLegacyPlanGroup = false } = useIsLegacyPlanGroup();
  const shouldFetch3PlPromotion = planDetail.data?.shouldFetch3PlPromotion(isStripeUser, isLegacyPlanGroup) ?? false;
  const promotionQuery = useUpgradePlanPromotion(shouldFetch3PlPromotion);
  const microEmail3PlGroupId = shouldFetch3PlPromotion
    ? promotionQuery.data?.getRecommended3PlPlanGroupId() ?? PLAN_GROUP_IDS.V21GBB_PLANGROUP_ID
    : undefined;

  // For paid Micro Email users, the add-ons planGroupId must match the overridden 3PL group (109).
  const addOnsQuery = useAddOnsFacade(planIdForAddOnsRequest, includePurchased, microEmail3PlGroupId);
  const billingAccountQuery = useBillingAccount();
  const paymentPlanQuery = usePaymentPlanFacade(isStripeUser);
  const { isLocked: isSingleBillChildAccount, isPending: isSingleBillChildAccountPending } =
    useIsSingleBillChildAccountState({ billingStrategy });

  const { lifecycle, ...componentProps } = useMemo(() => {
    const returnValue = initializer.addOnsInit({
      pageStore,
      queries: [billingAccountQuery, addOnsQuery, paymentPlanQuery, planDetail],
      t,
      billingStrategy: resolvedBillingStrategy,
      isSmsBundle,
      isValueFocusedMWebPlanSelectionEnabled,
      isSingleBillChildAccount: Boolean(isSingleBillChildAccount),
      isSingleBillChildAccountPending,
    });

    return {
      ...returnValue,
      externalHooks,
      hooks,
    };
  }, [
    initializer,
    pageStore,
    billingAccountQuery,
    addOnsQuery,
    paymentPlanQuery,
    planDetail,
    t,
    hooks,
    externalHooks,
    resolvedBillingStrategy,
    isSmsBundle,
    isValueFocusedMWebPlanSelectionEnabled,
    isSingleBillChildAccount,
    isSingleBillChildAccountPending,
    microEmail3PlGroupId,
  ]);

  useWidgetLifecycle(componentProps.status, lifecycle);

  return componentProps;
};

export type AddOnsWidgetProps = ReturnType<typeof useAddOnsWidget>;
